import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import EditIcon from "@mui/icons-material/Edit";
import { buildUpcomingItems, mergeClientDateReminderConfig } from "../../../../Utils/clientDateReminders";

const TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "birthday", label: "Birthday" },
  { value: "account_anniversary", label: "Account" },
  { value: "custom", label: "Custom" },
  { value: "institutional", label: "Institutional" },
];

const typeColor = {
  birthday: "#2af598",
  account_anniversary: "#7eb8ff",
  custom: "#ffb86b",
  institutional: "#c4b5fd",
};

/**
 * Upcoming relationship dates for Individual clients + institutional calendar.
 */
export default function UpcomingDatesTab({
  clients,
  config,
  onAddCustom,
  onEditCustom,
  onMuteType,
}) {
  const [typeFilter, setTypeFilter] = useState("all");
  const merged = useMemo(() => mergeClientDateReminderConfig(config), [config]);
  const items = useMemo(() => buildUpcomingItems(clients, merged), [clients, merged]);
  const filtered = useMemo(
    () => (typeFilter === "all" ? items : items.filter((i) => i.type === typeFilter)),
    [items, typeFilter]
  );

  const institutionalMonth = useMemo(() => {
    const today = new Date();
    const month = today.getMonth() + 1;
    return (merged.institutional_events || []).filter((ev) => {
      if (ev.enabled === false) return false;
      if (ev.variable === "last_sunday_march") return month === 3;
      if (ev.variable === "third_sunday_june") return month === 6;
      return Number(ev.month) === month;
    });
  }, [merged]);

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Filter</InputLabel>
          <Select
            label="Filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{
              color: "#fff",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
              ".MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" },
            }}
          >
            {TYPE_FILTERS.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>
          Next {merged.lookahead_days} days · Individual clients only · Digests to{" "}
          {merged.recipient_email}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAddCustom?.()}
          sx={{ color: "#2af598", textTransform: "none", border: "1px solid rgba(42,245,152,0.35)" }}
        >
          Add custom date
        </Button>
      </Box>

      {institutionalMonth.length > 0 && (
        <Paper
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: "rgba(196,181,253,0.08)",
            border: "1px solid rgba(196,181,253,0.25)",
          }}
        >
          <Typography sx={{ color: "#c4b5fd", fontSize: 12, fontWeight: 700, letterSpacing: 1, mb: 1 }}>
            INSTITUTIONAL THIS MONTH
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {institutionalMonth.map((ev) => (
              <Chip
                key={ev.id}
                size="small"
                label={ev.label}
                title={ev.tip_en || ""}
                sx={{ bgcolor: "rgba(196,181,253,0.15)", color: "#e9e0ff" }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {filtered.length === 0 ? (
        <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>
          No upcoming dates in the lookahead window.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "rgba(255,255,255,0.6)" }}>When</TableCell>
              <TableCell sx={{ color: "rgba(255,255,255,0.6)" }}>Type</TableCell>
              <TableCell sx={{ color: "rgba(255,255,255,0.6)" }}>Client</TableCell>
              <TableCell sx={{ color: "rgba(255,255,255,0.6)" }}>Detail</TableCell>
              <TableCell sx={{ color: "rgba(255,255,255,0.6)" }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((item) => (
              <TableRow key={item.key}>
                <TableCell sx={{ color: "#fff", whiteSpace: "nowrap" }}>
                  {item.date}
                  <Typography component="span" sx={{ color: "rgba(255,255,255,0.45)", fontSize: 12, ml: 1 }}>
                    {item.days_until === 0 ? "today" : `in ${item.days_until}d`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.label}
                    sx={{
                      height: 22,
                      fontSize: 11,
                      bgcolor: "rgba(255,255,255,0.06)",
                      color: typeColor[item.type] || "#fff",
                      border: `1px solid ${typeColor[item.type] || "rgba(255,255,255,0.2)"}`,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: "rgba(255,255,255,0.85)" }}>
                  {item.type === "institutional" ? (
                    <em style={{ opacity: 0.6 }}>Team tip</em>
                  ) : (
                    <>
                      {item.full_name}
                      <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
                        {item.email}
                      </Typography>
                      {item.do_not_contact ? (
                        <Chip size="small" label="Do not contact" color="warning" sx={{ mt: 0.5, height: 18, fontSize: 10 }} />
                      ) : null}
                    </>
                  )}
                </TableCell>
                <TableCell sx={{ color: "rgba(255,255,255,0.7)", fontSize: 13, maxWidth: 320 }}>
                  {item.detail}
                </TableCell>
                <TableCell align="right">
                  {item.type === "custom" && item.user_id ? (
                    <Tooltip title="Edit custom date">
                      <IconButton size="small" onClick={() => onEditCustom?.(item)} sx={{ color: "#7eb8ff" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {(item.type === "birthday" || item.type === "account_anniversary") && item.user_id ? (
                    <Tooltip title={`Mute ${item.type} reminders for this client`}>
                      <IconButton
                        size="small"
                        onClick={() => onMuteType?.(item.user_id, item.type)}
                        sx={{ color: "rgba(255,255,255,0.55)" }}
                      >
                        <VolumeOffIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
